import { useState } from "react";
import api from "../lib/api";
import PageHeader, { PageBody, Card, Badge, useFetch } from "../components/Shared";
import { toast } from "sonner";
import { Plus, FloppyDisk, Trash, Bell, Clock, ChartLine, Robot } from "@phosphor-icons/react";
import { usePipeline, pipelineParams } from "../lib/pipeline";

const PIPELINE_LABEL = { ambassador: "Ambassador", founder: "Founder" };
const SCHEDULE_TYPE_LABEL = {
  offset_hours: "Offset (horas tras evento)",
  offset_days: "Offset (días tras evento)",
  before_event_hours: "Antes del evento (horas)",
  cron: "Cron recurrente",
};

const DOW_LABELS = { mon: "Lun", tue: "Mar", wed: "Mié", thu: "Jue", fri: "Vie", sat: "Sáb", sun: "Dom" };

function describeSchedule(s) {
  if (!s) return "—";
  if (s.type === "cron") {
    const v = s.value || {};
    const dow = (v.day_of_week || "*").split(",").map((d) => DOW_LABELS[d] || d).join("+");
    return `${dow} ${String(v.hour ?? 0).padStart(2, "0")}:${String(v.minute ?? 0).padStart(2, "0")} UTC`;
  }
  const unit = s.type === "offset_hours" || s.type === "before_event_hours" ? "h" : "d";
  return `${s.value || 0}${unit}`;
}

export default function Reminders() {
  const { activePipelineSlug } = usePipeline();
  const { data: policies, loading, reload } = useFetch(
    () => api.get("/reminders", { params: pipelineParams(activePipelineSlug) }).then((r) => r.data),
    [activePipelineSlug]
  );
  const { data: agents } = useFetch(() => api.get("/agents").then((r) => r.data), []);
  const { data: pipelinesFull } = useFetch(() => api.get("/pipelines").then((r) => r.data), []);
  const { data: templates } = useFetch(() => api.get("/whatsapp/templates").then((r) => r.data), []);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  return (
    <>
      <PageHeader
        label="Operación"
        title="Recordatorios"
        description="Configura cuándo y con qué agente se mandan los recordatorios automáticos del sistema (video, acuerdo, inducción, check-ins, reactivaciones)."
      >
        <button onClick={() => setCreating(true)} data-testid="rem-create-btn" className="bg-terracotta text-white px-4 py-2 rounded text-sm flex items-center gap-2 hover:bg-terracotta-hover">
          <Plus size={14} /> Nueva política
        </button>
      </PageHeader>
      <PageBody>
        {loading && <div className="text-carbon-500">Cargando...</div>}
        {!loading && (
          <Card className="overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-carbon-50 border-b border-carbon-200">
                <tr className="text-left">
                  <th className="px-4 py-2 font-medium text-carbon-700">Pipeline</th>
                  <th className="px-4 py-2 font-medium text-carbon-700">Política</th>
                  <th className="px-4 py-2 font-medium text-carbon-700">Stage</th>
                  <th className="px-4 py-2 font-medium text-carbon-700">Cuándo</th>
                  <th className="px-4 py-2 font-medium text-carbon-700">Agente IA</th>
                  <th className="px-4 py-2 font-medium text-carbon-700">Estado</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-carbon-100">
                {(policies || []).map((p) => (
                  <tr key={p.id} className="hover:bg-carbon-50" data-testid={`rem-row-${p.policy_key}`}>
                    <td className="px-4 py-3">
                      <Badge tone={p.pipeline_slug === "ambassador" ? "forest" : "terracotta"}>
                        {PIPELINE_LABEL[p.pipeline_slug] || p.pipeline_slug || "—"}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-sm font-medium">{p.label}</div>
                      <div className="font-mono text-[10px] text-carbon-400">{p.policy_key}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-carbon-600 font-mono">{p.trigger_stage_key || "—"}</td>
                    <td className="px-4 py-3 text-xs">
                      <div className="font-mono">{describeSchedule(p.schedule)}</div>
                      <div className="text-[10px] text-carbon-500">{SCHEDULE_TYPE_LABEL[p.schedule?.type] || p.schedule?.type}</div>
                    </td>
                    <td className="px-4 py-3 text-xs">
                      {p.agent_name ? (
                        <div>
                          <div className="font-medium">{p.agent_name}</div>
                          <div className="text-[10px] text-carbon-500">{p.agent_provider}/{p.agent_model}</div>
                        </div>
                      ) : <span className="text-carbon-400">Sin agente · usa texto plano</span>}
                    </td>
                    <td className="px-4 py-3">
                      <Badge tone={p.active ? "forest" : "neutral"}>{p.active ? "Activa" : "Inactiva"}</Badge>
                      {p.built_in && <div className="text-[10px] text-carbon-400 mt-1">built-in</div>}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => setEditing(p)} data-testid={`rem-edit-${p.policy_key}`}
                        className="text-xs text-terracotta hover:underline">Editar</button>
                    </td>
                  </tr>
                ))}
                {(policies || []).length === 0 && (
                  <tr><td colSpan={7} className="text-center text-carbon-400 py-8">Sin políticas. Reinicia el backend para sembrar las built-in.</td></tr>
                )}
              </tbody>
            </table>
          </Card>
        )}
      </PageBody>

      {editing && <PolicyModal policy={editing} agents={agents} pipelinesFull={pipelinesFull} templates={templates} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
      {creating && <PolicyModal policy={null} agents={agents} pipelinesFull={pipelinesFull} templates={templates} onClose={() => setCreating(false)} onSaved={() => { setCreating(false); reload(); }} />}
    </>
  );
}

function PolicyModal({ policy, agents, pipelinesFull, templates, onClose, onSaved }) {
  const isNew = !policy;
  const { pipelines } = usePipeline();
  const selectablePipelines = (pipelines || []).filter((p) => p.slug !== "all");
  const [form, setForm] = useState(policy ? { ...policy } : {
    policy_key: "",
    label: "",
    description: "",
    pipeline_slug: "ambassador",
    trigger_stage_key: "",
    schedule: { type: "offset_hours", value: 24 },
    agent_id: "",
    template_name: "",
    active: true,
  });
  const [busy, setBusy] = useState(false);

  // Etapas del pipeline seleccionado (data-driven desde /pipelines)
  const currentPipeline = (pipelinesFull || []).find((p) => p.slug === form.pipeline_slug);
  const stageOptions = [...((currentPipeline?.stages) || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  // Templates disponibles para el pipeline (o globales sin pipeline)
  const templateOptions = (templates || []).filter(
    (t) => !t.pipeline_slug || t.pipeline_slug === form.pipeline_slug
  );

  const setSchedule = (patch) => setForm({ ...form, schedule: { ...form.schedule, ...patch } });
  const setCronValue = (patch) => setSchedule({ value: { ...(form.schedule.value || {}), ...patch } });

  const save = async () => {
    setBusy(true);
    try {
      if (isNew) {
        await api.post("/reminders", {
          ...form,
          agent_id: form.agent_id || null,
          template_name: form.template_name || null,
        });
        toast.success("Política creada");
      } else {
        await api.patch(`/reminders/${policy.id}`, {
          label: form.label, description: form.description,
          pipeline_slug: form.pipeline_slug, trigger_stage_key: form.trigger_stage_key,
          schedule: form.schedule, agent_id: form.agent_id || null,
          template_name: form.template_name || "",
          active: form.active,
        });
        toast.success("Política actualizada");
      }
      onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!confirm(`Borrar la política "${policy.label}"? Solo no built-in.`)) return;
    setBusy(true);
    try { await api.delete(`/reminders/${policy.id}`); toast.success("Borrada"); onSaved(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Error"); } finally { setBusy(false); }
  };

  const isCron = form.schedule.type === "cron";

  return (
    <div className="fixed inset-0 bg-carbon-900/60 z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-surface border border-carbon-200 rounded-lg w-full max-w-2xl p-6 my-8">
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="section-label flex items-center gap-1"><Bell size={12} /> {isNew ? "Nueva política" : "Editar política"}</div>
            <h2 className="font-heading text-xl">{policy?.label || "Política de recordatorio"}</h2>
            {policy?.built_in && <p className="text-xs text-carbon-500 mt-1">Built-in — solo timing y agente son editables.</p>}
          </div>
        </div>

        <div className="space-y-3">
          {isNew && (
            <label className="block">
              <span className="section-label">Clave interna (sin espacios)</span>
              <input value={form.policy_key} onChange={(e) => setForm({ ...form, policy_key: e.target.value.replace(/\s+/g, "_") })}
                data-testid="rem-policy-key-input"
                placeholder="ej. ambassador_celebration_30d"
                className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
            </label>
          )}

          <label className="block">
            <span className="section-label">Etiqueta visible</span>
            <input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
              data-testid="rem-label-input"
              className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
          </label>

          <label className="block">
            <span className="section-label">Descripción</span>
            <textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={2} className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm" />
          </label>

          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="section-label">Pipeline</span>
              <select value={form.pipeline_slug || ""} onChange={(e) => setForm({ ...form, pipeline_slug: e.target.value || null })}
                disabled={!isNew && policy?.built_in}
                data-testid="rem-pipeline-select"
                className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                <option value="">— ninguno —</option>
                {selectablePipelines.map((p) => (
                  <option key={p.slug} value={p.slug}>{p.short || p.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="section-label">Stage trigger</span>
              {stageOptions.length > 0 ? (
                <select value={form.trigger_stage_key || ""} onChange={(e) => setForm({ ...form, trigger_stage_key: e.target.value })}
                  disabled={!isNew && policy?.built_in}
                  data-testid="rem-stage-select"
                  className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                  <option value="">— Selecciona una etapa —</option>
                  {stageOptions.map((s) => (
                    <option key={s.stage_key} value={s.stage_key}>{s.name} ({s.stage_key})</option>
                  ))}
                </select>
              ) : (
                <input value={form.trigger_stage_key || ""} onChange={(e) => setForm({ ...form, trigger_stage_key: e.target.value })}
                  disabled={!isNew && policy?.built_in}
                  placeholder={form.pipeline_slug ? "Este pipeline no tiene etapas" : "Selecciona un pipeline primero"}
                  data-testid="rem-stage-input"
                  className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
              )}
            </label>
          </div>

          <label className="block">
            <span className="section-label flex items-center gap-1"><Bell size={12} /> Template de WhatsApp</span>
            <select value={form.template_name || ""} onChange={(e) => setForm({ ...form, template_name: e.target.value })}
              data-testid="rem-template-select"
              className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
              <option value="">— Sin template (usa agente / texto plano) —</option>
              {templateOptions.map((t) => (
                <option key={t.id || t.name} value={t.name}>
                  {t.name} · {t.language || "es"}{t.status ? ` · ${t.status}` : ""}
                </option>
              ))}
            </select>
            <p className="text-[11px] text-carbon-500 mt-1">
              Si eliges un template, el recordatorio se enviará con esa plantilla aprobada. Puedes crear varias políticas para la misma etapa (con distinta clave interna) para enviar más de un recordatorio.
            </p>
          </label>

          <div className="border border-carbon-200 rounded p-3 bg-carbon-50">
            <div className="section-label mb-2 flex items-center gap-1"><Clock size={12} /> Cuándo dispara</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="text-xs text-carbon-500">Tipo</span>
                <select value={form.schedule.type} onChange={(e) => setSchedule({ type: e.target.value, value: e.target.value === "cron" ? { day_of_week: "tue,fri", hour: 9, minute: 0 } : 24 })}
                  data-testid="rem-schedule-type"
                  className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
                  <option value="offset_hours">Horas tras evento</option>
                  <option value="offset_days">Días tras evento</option>
                  <option value="before_event_hours">Horas antes del evento</option>
                  <option value="cron">Cron recurrente</option>
                </select>
              </label>
              {!isCron ? (
                <label className="block">
                  <span className="text-xs text-carbon-500">Valor</span>
                  <input type="number" min={0} value={form.schedule.value || 0}
                    onChange={(e) => setSchedule({ value: parseInt(e.target.value) || 0 })}
                    data-testid="rem-schedule-value"
                    className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm font-mono" />
                </label>
              ) : (
                <div className="space-y-2 col-span-1">
                  <label className="block">
                    <span className="text-xs text-carbon-500">Días (coma separados)</span>
                    <input value={(form.schedule.value && form.schedule.value.day_of_week) || ""}
                      onChange={(e) => setCronValue({ day_of_week: e.target.value })}
                      placeholder="tue,fri"
                      className="mt-1 w-full bg-page border border-carbon-200 rounded px-2 py-1 text-xs font-mono" />
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <label className="block">
                      <span className="text-xs text-carbon-500">Hora UTC</span>
                      <input type="number" min={0} max={23} value={(form.schedule.value && form.schedule.value.hour) || 0}
                        onChange={(e) => setCronValue({ hour: parseInt(e.target.value) || 0 })}
                        className="mt-1 w-full bg-page border border-carbon-200 rounded px-2 py-1 text-xs font-mono" />
                    </label>
                    <label className="block">
                      <span className="text-xs text-carbon-500">Min UTC</span>
                      <input type="number" min={0} max={59} value={(form.schedule.value && form.schedule.value.minute) || 0}
                        onChange={(e) => setCronValue({ minute: parseInt(e.target.value) || 0 })}
                        className="mt-1 w-full bg-page border border-carbon-200 rounded px-2 py-1 text-xs font-mono" />
                    </label>
                  </div>
                </div>
              )}
            </div>
            <p className="text-[11px] text-carbon-500 mt-2">
              Preview: <code className="font-mono">{describeSchedule(form.schedule)}</code>
            </p>
          </div>

          <label className="block">
            <span className="section-label flex items-center gap-1"><Robot size={12} /> Agente IA que genera el mensaje</span>
            <select value={form.agent_id || ""} onChange={(e) => setForm({ ...form, agent_id: e.target.value })}
              data-testid="rem-agent-select"
              className="mt-1 w-full bg-page border border-carbon-200 rounded px-3 py-2 text-sm">
              <option value="">— Sin agente (texto plano hardcoded) —</option>
              {(agents || []).filter((a) => !a.internal_only).map((a) => (
                <option key={a.id} value={a.id}>{a.name} · {a.provider}/{a.model}</option>
              ))}
            </select>
            <p className="text-[11px] text-carbon-500 mt-1">Si dejas vacío, el sistema usa el texto fijo que ya viene en el código.</p>
          </label>

          <label className="inline-flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })}
              data-testid="rem-active-toggle" />
            Política activa
          </label>
        </div>

        <div className="flex items-center justify-end gap-2 mt-5 pt-4 border-t border-carbon-100">
          {!isNew && !policy?.built_in && (
            <button onClick={remove} disabled={busy} className="text-xs text-signal hover:underline flex items-center gap-1 mr-auto">
              <Trash size={12} /> Borrar
            </button>
          )}
          <button onClick={onClose} disabled={busy} className="text-sm px-4 py-2 hover:bg-carbon-100 rounded">Cancelar</button>
          <button onClick={save} disabled={busy} data-testid="rem-save-btn"
            className="text-sm px-4 py-2 bg-terracotta text-white rounded flex items-center gap-1 hover:bg-terracotta-hover disabled:opacity-50">
            <FloppyDisk size={12} /> {busy ? "Guardando..." : "Guardar"}
          </button>
        </div>
      </div>
    </div>
  );
}
